import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api, formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const { items, subtotalInr, clear } = useCart();
  const { currency } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [discountCode, setDiscountCode] = useState("");
  const [discountValid, setDiscountValid] = useState(null); // { code, discount_inr }
  const [address, setAddress] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "India",
  });

  const shippingInr = subtotalInr >= 20000 ? 0 : (shippingMethod === "express" ? 799 : 299);
  const discountInr = discountValid?.discount_inr || 0;
  const totalInr = Math.max(0, subtotalInr + shippingInr - discountInr);

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    try {
      const { data } = await api.post("/discount/validate", {
        code: discountCode.trim(),
        subtotal_inr: subtotalInr,
      });
      setDiscountValid(data);
      toast.success(`${data.code} applied — you saved ${formatPrice(data.discount_inr, currency)}.`);
    } catch (e) {
      setDiscountValid(null);
      toast.error(e?.response?.data?.detail || "Invalid code.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Your bag is empty."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items,
        shipping_address: address,
        discount_code: discountValid?.code || null,
        currency,
        shipping_method: shippingMethod,
        origin_url: window.location.origin,
      });
      // Persist order id so success page knows what to poll (already in URL)
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="pt-40 pb-40 text-center" data-testid="checkout-empty">
        <p className="font-serif text-4xl">Your bag is empty.</p>
        <button onClick={() => navigate("/")} className="mt-6 btn-editorial-ghost">Continue shopping</button>
      </main>
    );
  }

  return (
    <main className="pt-24 md:pt-32 pb-24" data-testid="checkout-page">
      <div className="px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-10">
        <form onSubmit={submit} className="md:col-span-7 space-y-10" data-testid="checkout-form">
          <div>
            <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">Checkout</p>
            <h1 className="font-serif text-4xl md:text-5xl mt-2">Shipping Details</h1>
          </div>

          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            {[
              ["full_name", "Full name"],
              ["email", "Email"],
              ["phone", "Phone"],
              ["line1", "Address line 1"],
              ["line2", "Address line 2 (optional)"],
              ["city", "City"],
              ["state", "State / Province"],
              ["postal_code", "Postal code"],
              ["country", "Country"],
            ].map(([k, label]) => (
              <label key={k} className={k === "line1" || k === "line2" ? "md:col-span-2 block" : "block"}>
                <span className="text-[11px] uppercase tracking-nav text-[#7A7A7A]">{label}</span>
                <input
                  className="input-underline"
                  required={!["line2"].includes(k)}
                  value={address[k]}
                  type={k === "email" ? "email" : "text"}
                  onChange={(e) => setAddress({ ...address, [k]: e.target.value })}
                  data-testid={`checkout-${k}`}
                />
              </label>
            ))}
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mb-3">Shipping Method</p>
            <div className="space-y-2">
              {[
                { k: "standard", label: "Standard · 5–8 business days", cost: 299 },
                { k: "express", label: "Express · 2–4 business days", cost: 799 },
              ].map((s) => (
                <label
                  key={s.k}
                  className={`flex items-center justify-between border p-4 cursor-pointer transition-colors ${shippingMethod === s.k ? "border-[#5C1A1B]" : "border-[#BCA37F]/50"}`}
                  data-testid={`checkout-ship-${s.k}`}
                >
                  <div className="flex items-center gap-3">
                    <input type="radio" checked={shippingMethod === s.k} onChange={() => setShippingMethod(s.k)} className="accent-[#5C1A1B]" />
                    <span className="text-sm">{s.label}</span>
                  </div>
                  <span className="text-sm text-[#4A4A4A]">
                    {subtotalInr >= 20000 ? "Free" : formatPrice(s.cost, currency)}
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-[11px] uppercase tracking-nav text-[#7A7A7A]">Orders over ₹20,000 ship free.</p>
          </div>

          <button type="submit" disabled={loading} className="btn-editorial w-full" data-testid="checkout-submit">
            {loading ? "Redirecting to secure payment…" : `Pay ${formatPrice(totalInr, currency)}`}
          </button>
          <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A]">
            Secure checkout by Stripe · Cards, Apple Pay & Google Pay accepted.
          </p>
        </form>

        <aside className="md:col-span-5">
          <div className="bg-[#F3EFEA]/60 p-6 md:p-8" data-testid="checkout-summary">
            <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B] mb-3">Order Summary</p>
            <ul className="divide-y divide-[#BCA37F]/25">
              {items.map((i) => (
                <li key={`${i.product_id}__${i.size}`} className="py-4 flex gap-4">
                  <img src={i.image} alt={i.name} className="w-16 h-20 object-cover" />
                  <div className="flex-1">
                    <p className="font-serif text-lg leading-tight">{i.name}</p>
                    <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mt-1">Size {i.size} · Qty {i.quantity}</p>
                  </div>
                  <p className="text-sm">{formatPrice(i.price_inr * i.quantity, currency)}</p>
                </li>
              ))}
            </ul>

            <div className="mt-4 flex gap-2">
              <input
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                placeholder="Discount code (WELCOME10)"
                className="input-underline"
                data-testid="checkout-discount-input"
              />
              <button type="button" onClick={applyDiscount} className="text-[11px] uppercase tracking-nav text-[#5C1A1B] hover:text-[#1A1A1A]" data-testid="checkout-discount-apply">
                Apply
              </button>
            </div>

            <dl className="mt-6 space-y-2 text-sm">
              <div className="flex justify-between"><dt>Subtotal</dt><dd className="font-mono-alt">{formatPrice(subtotalInr, currency)}</dd></div>
              <div className="flex justify-between"><dt>Shipping</dt><dd className="font-mono-alt">{shippingInr === 0 ? "Free" : formatPrice(shippingInr, currency)}</dd></div>
              {discountInr > 0 && (
                <div className="flex justify-between text-[#5C1A1B]"><dt>Discount</dt><dd className="font-mono-alt">-{formatPrice(discountInr, currency)}</dd></div>
              )}
              <div className="flex justify-between pt-3 border-t border-[#BCA37F]/25 text-base">
                <dt className="font-serif text-xl">Total</dt>
                <dd className="font-mono-alt text-lg" data-testid="checkout-total">{formatPrice(totalInr, currency)}</dd>
              </div>
            </dl>
          </div>
        </aside>
      </div>
    </main>
  );
}
